import { ProjectsAvailableSessionTypes, ProjectTeamRole } from "./Projects";
import { ProjectQuizBucket } from "./Quiz";
import {
  Label,
  MainSpecializations,
  SpecialistData,
  SpecialistLevel,
  SpecialistTag,
} from "./TypeSpecialist";
import { GeneralMultiLangText, TypeUserData } from "./TypeUsers";

export type TypeNewCompany = {
  name: string;
  description?: { language: string; text: string }[];
  avatar?: string;
  logoS3?: string;
  clients?: string[];
  specialists?: string[];
  isBarterAvailable?: boolean;
};

export type Company = {
  _id: string;
  name: string;
  logo?: string;
  logoS3?: string;
  description?: { language: string; text: string }[];
  avatar?: string;
  owner: TypeUserData;
  managers: TypeUserData[];
  clients: TypeUserData[];
  specialists: TypeUserData[];
  projects: Project[];
  entryCodes: {
    code: string;
    role: Roles.DEEFAULT_USER | Roles.SPECIALIST | Roles.MANAGER;
    forEmail: string;
  }[];
  entryCodeWithoutEmail?: {
    code: string;
    usesAvailable: number;
  };
  isBarterAvailable: boolean;
};

export enum ProjectRoles {
  DEEFAULT_USER = "DEEFAULT_USER",
  SPECIALIST = "SPECIALIST",
}

export type Project = {
  _id: string;
  shortId: string;
  title: Label[];
  description: Label[];
  adminRemarkForExperts?: Label[];
  briefDescription?: Label[];
  isPrivate: boolean;
  isPublicAccess?: boolean;
  avatar?: string;
  creationDate?: Date;
  endDate?: Date;
  clientLimit: number;
  specialistLimit: number;
  clients: {
    _id: string;
    name: GeneralMultiLangText[];
    surname: GeneralMultiLangText[];
  }[];
  specialists: {
    _id: string;
    name: GeneralMultiLangText[];
    surname: GeneralMultiLangText[];
  }[];
  realTimeSessionDiscount: number;
  video?: {
    language: string;
    video?: string;
    videoS3?: string;
    link?: string;
  }[];
  treeQuizTagsToSkip?: SpecialistTag[];
  availableSessionTypes?: {
    type: ProjectsAvailableSessionTypes;
    amount: number;
    unlimited: boolean;
    specialization: MainSpecializations;
  }[];
  isBarter: boolean;
  specializations?: MainSpecializations[];
  entryCodes: {
    code: string;
    roles: ProjectRoles[];
    forSpecialist?: string;
    forEmail?: string;
    forSpecializations?: MainSpecializations[];
    isAccepted: boolean;
    isRejected: boolean;
    sentAt: string;
    answeredAt: string;
    user: {
      _id: string;
      name: GeneralMultiLangText[];
      surname: GeneralMultiLangText[];
    };
  }[];
  pricing?: {
    price?: number;
    discount?: number;
    tax?: number;
    level: SpecialistLevel;
  }[];
  packagePricing?: {
    price?: number;
    discount?: number;
    tax?: number;
    level: string;
  }[];
  rescheduleOptions?: {
    clientCancelThreshold?: number;
    specialistCancelThreshold?: number;
    clientRescheduleThreshold?: number;
    clientCancelPackageThreshold?: number;
    clientReschedulePackageThreshold?: number;
    clientReschedulePackageAmount?: number;
  };
  skipSpecialistConfirmation?: boolean;
  skipSpecialistRecordConfirmation?: boolean;
  enableTwilioSms: boolean;
  tags: SpecialistTag[];
  allowSpecialistLeave: boolean;
  allowSpecialistPause: boolean;
  pausedSpecialists?: string[];
  leaveLogs?: {
    date?: Date;
    specialist: string;
  }[];
  packageTax?: number;
  sessionTax?: number;
  staticSessionPrices: boolean;
  staticPackagePrices: boolean;
  staticPackages?: {
    _id: string;
    package: string;
    specialization: string;
  }[];
  offerPromoPackages?: boolean;
  invitationByLinkCode?: string;
  team?: {
    _id: string;
    user: TypeUserData;
    roles: (ProjectTeamRole | string)[];
  }[];
  quizBuckets?: ProjectQuizBucket[];
};

export enum CompanyRoles {
  OWNER = "owner",
  SPECIALIST = "specialist",
  MANAGER = "manager",
  PROJECT_TEAM_MEMBER = "project-team-member",
  EMPLOYEE = "employee",
}

export type TypeCompanyParticipantsFilters = {
  search?: string;
  project?: string | string[];
  role?: CompanyRoles | CompanyRoles[];
};

export type TypeCompanyEmployee = {
  user: {
    name: GeneralMultiLangText[];
    surname: GeneralMultiLangText[];
    _id: string;
    email: string;
    projects: {
      _id: string;
      title: Label[];
    }[];

    specialistIds?: ProjectParticipantSpecialist[];
  };
  role: { value: string; translatedValue: string };
};

type ProjectParticipantSpecialist = Omit<
  SpecialistData,
  "mainSpecializations" | "level"
> & {
  _id: string;
  mainSpecializations: (MainSpecializations & { _id: string })[];
  level?: SpecialistLevel & { _id: string };
};

export type TypeProjectParticipant = {
  _id: string;
  name?: TypeUserData["name"];
  surname?: TypeUserData["surname"];
  email?: TypeUserData["email"];
  shortId: TypeUserData["shortId"];
  specialistIds?: ProjectParticipantSpecialist[];
  roles: ("employee" | "specialist")[];
};

export type TypeCompanyStatistic = {
  employees: number;
  finishedSessions: number;
  futureSessions: number;
};
export enum Roles {
  ADMIN = "ADMIN",
  DEEFAULT_USER = "DEEFAULT_USER",
  SPECIALIST = "SPECIALIST",
  PSYCHOLOGIST = "PSYCHOLOGIST",
  COACH = "COACH",
  MENTOR = "MENTOR",
  MANAGER = "MANAGER",
}
export type MyProjectType = Omit<Project, "clients" | "specialists"> & {
  clients: string[];
  specialists: string[];
};
